import React, { useLayoutEffect, useState } from 'react'
import { useMount } from 'ahooks'
import Cookies from 'js-cookie'
import { Authorized } from '../Authorized'
import { useDispatch } from '@/store'
import { fetchDictionary, fetchFilterCountry, queryProviderLogo } from '@/store/dictionarySlice'
import type { AuthorizedProps } from '../Authorized'
import { fetchStationTypes, splitSupportCountryMapCreator } from '@/store/addressSlice'
import { fetchGuideInfo, fetchUserSetting, getMessageCenterConfig } from '@/store/commonSlice'
import { useUserLogin } from '../../hooks'
import Fallback from '@/components/Fallback'
import { markTTI } from '@/services/performance'
import { initUserPermissionTree } from '@/store/authSlice'
import { getActivityAction } from '@/store/giveawaySlice'
import { ABTEST_VERSION_KEY, ABTEST_VERSION_STABLE_VALUE, useAppVersionDetecter } from '@/components/ABTest'

export type LoginAuthorizedProps = Omit<AuthorizedProps, 'isReady' | 'isPermit'>

export const LoginAuthorized: React.FC<LoginAuthorizedProps> = (props) => {
  const dispatch = useDispatch()
  const [isReady, setIsReady] = useState(false)
  const { userLogin } = useUserLogin()
  const detectAppVersion = useAppVersionDetecter()

  useMount(() => {
    ;(async () => {
      setIsReady(false)
      try {
        // 先预设用户ABtest版本为稳定版
        if (!Cookies.get(ABTEST_VERSION_KEY)) {
          Cookies.set(ABTEST_VERSION_KEY, ABTEST_VERSION_STABLE_VALUE, { path: '/', expires: 10 })
        }
        // 校验登陆状态及国家版本
        await userLogin()
        // 确认用户真正的ABtest版本，若版本不是稳定版，则刷新页面
        detectAppVersion()

        dispatch(fetchUserSetting())

        /**
         * 这里用setTimeout是兼容如下场景：
         * 刚注册完跳转到首页，这时发出请求列表，还没后端初始化完，只会返回空的数据。
         * 初始化完后，后端会发一个推送，但此时socket又没初始化完，就会错过那次推送，导致的结果就是这两次数据都是空的
         */
        dispatch(fetchGuideInfo()).then((guideData) => {
          if (guideData === null) {
            setTimeout(() => dispatch(fetchGuideInfo()), 2000)
          }
        })

        dispatch(queryProviderLogo())
        dispatch(fetchStationTypes())
        dispatch(splitSupportCountryMapCreator())
        dispatch(getMessageCenterConfig())

        await Promise.all([dispatch(fetchDictionary()), dispatch(initUserPermissionTree()), dispatch(getActivityAction())])

        /** 收/发件国家过滤 */
        await dispatch(fetchFilterCountry())
        setIsReady(true)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        if (navigator.onLine) {
          // 网络状况没问题，就3秒后刷新重试
          setTimeout(() => {
            location.reload()
          }, 3000)
        } else {
          // 网络有问题，当重新在线时刷新页面
          window.addEventListener('online', () => {
            location.reload()
          })
        }
      }
    })()
  })
  useLayoutEffect(() => {
    // 初始化完成后标记tti时间
    if (isReady) markTTI()
  }, [isReady])

  return <Authorized {...props} isReady={isReady} isPermit={isReady} fallback={<Fallback />} />
}

LoginAuthorized.displayName = 'LoginAuthorized'
